import registry from '../apps.json';

export interface AppDefinition {
  id: string;
  name: string;
  shortName?: string;
  /** Path prefix on the shared domain, always with leading and trailing slash. */
  base: string;
  devPort: number;
  enabled: boolean;
  /** The game system this app hosts (matches campaigns.system). */
  system?: string;
}

export const apps: readonly AppDefinition[] = registry;
export type AppId = (typeof registry)[number]['id'];

export function getApp(id: AppId): AppDefinition {
  const app = apps.find((a) => a.id === id);
  if (!app) throw new Error(`Unknown app "${id}"`);
  return app;
}

/** The app that hosts a game system's pages, e.g. 'w40k-crusade' → 'w40k'. */
export function systemAppId(system: string): AppId | undefined {
  return apps.find((a) => a.system === system)?.id as AppId | undefined;
}

/**
 * Build a link into another app on the same domain (ADR 0008). Cross-app navigation is a full page load,
 * so use a plain <a href>, not the router's <Link>.
 *
 *   appUrl('w40k', '/rosters/123') → '/40k/rosters/123'
 */
export function appUrl(id: AppId, path = '/'): string {
  const { base } = getApp(id);
  const clean = path.replace(/^\/+/, '');
  return `${base}${clean}`;
}

/** Only allow same-origin relative paths as post-sign-in redirects (ADR 0009). */
export function safeNextPath(next: string | null | undefined, fallback = '/'): string {
  if (!next?.startsWith('/') || next.startsWith('//') || next.includes('\\')) return fallback;
  return next;
}
